// src/api/csvUpload.js
import { apiPost, extractMessage, api } from "../client";

/**
 * Invoice Finance import endpoint.
 * Override via REACT_APP_INVOICE_FINANCE_IMPORT_EXCEL if your backend uses a different path.
 * Must exist on the Frappe server your app proxies to (package.json proxy / FRAPPE_URL).
 */
export const INVOICE_FINANCE_IMPORT_EXCEL =
  process.env.REACT_APP_INVOICE_FINANCE_IMPORT_EXCEL ||
  "/api/method/dynesourcerapi.dynesourcerapi.doctype.invoice_finance.invoice_finance_api.import_excel";

/**
 * Convert Date object or ISO/datetime-local string to Frappe's expected format
 * Frappe expects: "YYYY-MM-DD HH:MM:SS"
 */
function formatDateTimeForFrappe(dateInput) {
  if (!dateInput) {
    throw new Error("No date provided for date_time.");
  }

  const date =
    typeof dateInput === "string" ? new Date(dateInput) : dateInput;

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.error("❌ Invalid date value passed to formatDateTimeForFrappe:", dateInput);
    throw new Error("Invalid datetime value. Please pick a valid date & time.");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // ✅ No "T" or "Z" – matches Frappe's expected format
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Step 1 — Upload CSV to File doctype
 */
export async function uploadCsvFile(file, onProgress = null) {
  if (!file) {
    throw new Error("No file provided for upload.");
  }

  // Ensure file has a name
  const fileName = file.name || file.filename || "upload.csv";
  
  const formData = new FormData();
  // Append file with explicit filename
  formData.append("file", file, fileName);
  
  // ✅ Add required fields for Frappe File doctype (required in production)
  // file_name is mandatory - Frappe needs this to create the File record
  formData.append("file_name", fileName);
  
  // ✅ Don't append doctype/docname for standalone files
  // Frappe's upload_file can work without these for standalone file uploads
  // Only include them if we're attaching to a specific document
  // Empty docname causes "Attached To Name must be a string or an integer" error
  
  // Debug: Log FormData contents (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log("📤 Uploading CSV file:", {
      fileName,
      fileSize: file.size,
      fileType: file.type,
      formDataKeys: Array.from(formData.keys())
    });
  }

  // Use api.post directly to support progress callbacks
  // Use relative URL (BASE is empty string, so path is used as-is)
  const url = "/api/method/upload_file";
  const config = {};
  
  if (onProgress) {
    config.onUploadProgress = (progressEvent) => {
      if (progressEvent && typeof progressEvent.loaded === 'number') {
        if (progressEvent.total && progressEvent.total > 0) {
          // Cap at 99% until upload is actually complete
          const percentCompleted = Math.min(99, Math.round((progressEvent.loaded * 100) / progressEvent.total));
          onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
        } else if (progressEvent.lengthComputable === false && progressEvent.loaded > 0) {
          // Conservative estimate if total is unknown
          const estimatedPercent = Math.min(90, Math.round((progressEvent.loaded / (1024 * 1024)) * 10));
          onProgress(estimatedPercent, progressEvent.loaded, progressEvent.total);
        }
      }
    };
  }

  const res = await api.post(url, formData, config);
  const raw = res.data;
  const msg = extractMessage(raw) || raw;

  const fileUrl =
    msg?.file_url ||
    msg?.message?.file_url ||
    msg?.data?.file_url ||
    null;

  if (!fileUrl) {
    console.error("❌ upload_file response missing file_url:", msg);
    throw new Error("Upload succeeded but file_url is missing from response.");
  }

  // Set to 100% when upload completes
  if (onProgress) {
    onProgress(100);
  }

  console.log("✅ CSV file uploaded successfully:", fileUrl);
  return {
    file_url: fileUrl,
    ...msg,
  };
}

/**
 * Step 2 — Trigger bulk import using uploaded file_url
 *
 * Backend: dynesourcerapi.multi_product.import_mortgage_products
 * The backend receives file_url and parses the CSV server-side.
 *
 * CSV Yes/No fields (single-column format in new templates):
 * - Single column: main header e.g. "Overpayments", subheader "Yes/No" or "Yes/no", cell value "Yes" or "No"
 * - Two columns (legacy): subheaders "Yes" and "No", one cell marked
 *
 * Backend must map these CSV columns into the product doctype so the UI shows the right value:
 * - "Portable" → portable (0/1)
 * - "Overpayments" → overpayments or overpayents (0/1) — UI checks both spellings
 * - "Underpayments" → underpayments (0/1)
 * - "Payment Holiday" → payment_holiday (0/1)
 * - "Free Legals" → free_legals (0/1)
 * - "Free Valuation" → free_valuation (0/1)
 * For single-column format: if cell value is "Yes" set 1, else 0.
 */
export async function importMortgageProducts(options = {}) {
  const {
    file_url,
    product_type,
    schedule_enabled,
    schedule_at,
    lender_id,
    lender_name,
    lender_email,
    product_categories,
  } = options;

  if (!file_url) {
    throw new Error("file_url is required to import mortgage products.");
  }

  const body = { file_url };

  // Optional – if backend uses it
  if (product_type) {
    body.product_type = product_type;
  }

  // Per-product category keyed by Lender Product Code: "BTL" or "CBTL" (Complex BTL columns filled)
  if (product_categories && Object.keys(product_categories).length > 0) {
    body.product_categories = JSON.stringify(product_categories);
  }

  // Lender information for admin portal imports
  if (lender_id) {
    body.lender_id = lender_id;
  }
  if (lender_name) {
    body.lender_name = lender_name;
  }
  if (lender_email) {
    body.lender_email = lender_email;
  }

  // product_schedule is ALWAYS required by backend
  // 0 = Immediate, 1 = Scheduled
  body.product_schedule = schedule_enabled ? 1 : 0;

  // date_time is ALWAYS required by backend (even for immediate)
  if (schedule_enabled) {
    if (!schedule_at) {
      throw new Error("schedule_at is required when scheduling is enabled.");
    }
    body.date_time = formatDateTimeForFrappe(schedule_at);
  } else {
    body.date_time = formatDateTimeForFrappe(new Date());
  }

  console.log("📤 Import request body:", body);

  const raw = await apiPost(
    "/api/method/dynesourcerapi.multi_product.import_mortgage_products",
    body
  );

  const message = extractMessage(raw) || raw;
  // Return full context so UI can show backend validation errors (e.g. results[].error, _server_messages)
  return {
    ...(typeof message === "object" && message !== null ? message : { message }),
    _server_messages: raw._server_messages,
    _raw: raw,
  };
}

/**
 * High-level helper — Upload CSV + Import Products
 */
export async function uploadAndImportMortgageProducts(options = {}) {
  const {
    file,
    product_type,
    scheduleEnabled,
    scheduleAt,
    lender_id,
    lender_name,
    lender_email,
    onProgress,
  } = options;

  if (!file) {
    throw new Error("Please select a file before processing upload.");
  }

  // 1) Upload CSV
  const uploadResult = await uploadCsvFile(file, onProgress);

  // 2) Trigger import
  const importResult = await importMortgageProducts({
    file_url: uploadResult.file_url,
    product_type,
    schedule_enabled: !!scheduleEnabled,
    schedule_at: scheduleAt,
    lender_id,
    lender_name,
    lender_email,
  });

  return {
    upload: uploadResult,
    imported: importResult,
  };
}

/**
 * Invoice Finance — import products from an uploaded file URL.
 * Backend: invoice_finance_api.import_excel
 */
export async function importInvoiceFinanceProducts({ file_url } = {}) {
  if (!file_url) {
    throw new Error("file_url is required to import invoice finance products.");
  }

  const raw = await apiPost(INVOICE_FINANCE_IMPORT_EXCEL, { file_url });
  const message = extractMessage(raw) || raw;

  if (process.env.NODE_ENV === "development") {
    console.log("✅ Invoice Finance import_excel response:", message);
  }

  return {
    ...(typeof message === "object" && message !== null ? message : { message }),
    _raw: raw,
  };
}

/**
 * Invoice Finance — upload file to Frappe, then import via import_excel.
 * Backend: invoice_finance_api.import_excel
 */
export async function uploadAndImportInvoiceFinanceProducts(file, onProgress = null) {
  if (!file) {
    throw new Error("Please select a file before processing upload.");
  }

  const uploadResult = await uploadCsvFile(file, (percent, loaded, total) => {
    if (onProgress) {
      onProgress(Math.min(90, Math.round(percent * 0.9)), loaded, total);
    }
  });

  if (onProgress) {
    onProgress(95);
  }

  const importResult = await importInvoiceFinanceProducts({
    file_url: uploadResult.file_url,
  });

  if (onProgress) {
    onProgress(100);
  }

  return importResult;
}
